/* ═══════════════════════════════════════════════════════════════
   Permissions Registry — Granular permission keys, categories,
   and helpers for the role-based access control system.

   Pattern: `resource.action` (e.g. `specs.publish`, `tasks.edit_any`)

   Single source of truth. The backend derives its canonical list
   from here (ec2-api imports this module). Frontend gates all use
   hasPermission() against these keys. Custom roles in the admin
   center are composed from this exact set.

   Design principles:
   - Every user-visible action should have a key here — "damn near
     every action gated" was the explicit product direction.
   - Distinguish `.view_own / .view_team / .view_all` wherever
     visibility matters (orgs rarely want everyone seeing everything).
   - Distinguish `.edit_own / .edit_any` wherever "edit my own record"
     is a different capability from "edit anyone's record".
   - Review-workflow actions (`submit_for_review / approve / reject`)
     get their own keys so publisher roles can exist without edit.
   ═══════════════════════════════════════════════════════════════ */

import type { AppModule } from './modules';

// ─── Permission Category (for grouped UI display) ───

export interface PermissionDef {
  key: string;
  label: string;
  description?: string;
}

export interface PermissionCategory {
  id: string;
  label: string;
  description: string;
  permissions: PermissionDef[];
}

// ─── Full Permission Registry ───

export const PERMISSION_CATEGORIES: PermissionCategory[] = [
  // ──────────────────────────────────────────────────────────────
  // ADMIN CENTER — firm-level configuration
  // ──────────────────────────────────────────────────────────────
  {
    id: 'admin_center',
    label: 'Admin Center',
    description: 'Access to firm-level settings and configuration',
    permissions: [
      {
        key: 'admin_center.view',
        label: 'View Admin Center',
        description: 'Access the Admin Center page',
      },
      {
        key: 'admin_center.manage_roles',
        label: 'Manage Roles & Privileges',
        description: 'Create, edit, and delete privilege roles',
      },
      {
        key: 'admin_center.manage_billing',
        label: 'Manage Billing Setup',
        description:
          'Billing roles, fee structures, billing codes, phases, overhead categories, expense types',
      },
      {
        key: 'admin_center.manage_subscription',
        label: 'Manage Subscription',
        description: 'View and manage firm subscription tier and enabled modules',
      },
      {
        key: 'admin_center.manage_firm_settings',
        label: 'Manage Firm Settings',
        description:
          'Firm name, logo, default working hours, table column defaults, and other firm-wide preferences',
      },
      { key: 'admin_center.manage_project_types', label: 'Manage Project Types' },
      { key: 'admin_center.manage_phases', label: 'Manage Default Phases' },
      { key: 'admin_center.manage_activity_codes', label: 'Manage Activity Codes' },
      { key: 'admin_center.manage_holidays', label: 'Manage Firm Holidays' },
      {
        key: 'admin_center.manage_departments',
        label: 'Manage Departments',
        description: 'Department lookup (MEPT, BIM, etc) used by the team page and forecast grid',
      },
      { key: 'admin_center.manage_vacation_categories', label: 'Manage Vacation Categories' },
      {
        key: 'admin_center.manage_company_roles',
        label: 'Manage Company Roles',
        description: 'Job titles / company-side role labels used for directory display',
      },
      {
        key: 'admin_center.manage_billing_roles',
        label: 'Manage Billing Roles',
        description: 'Timesheet role categories and default rates',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // TEAM MEMBERS
  // ──────────────────────────────────────────────────────────────
  {
    id: 'team_members',
    label: 'Team Members',
    description: 'Manage people in your firm',
    permissions: [
      { key: 'team_members.view', label: 'View Team' },
      {
        key: 'team_members.view_details',
        label: 'View Sensitive Details',
        description: 'See compensation, bill rates, and other restricted fields',
      },
      // `team_members.create` also gates sending an invitation (the invite
      // row IS the create) — /api/invitations/send checks this key.
      { key: 'team_members.create', label: 'Add Members' },
      { key: 'team_members.edit', label: 'Edit Members' },
      {
        key: 'team_members.edit_own',
        label: 'Edit Own Profile',
        description: 'Everyone needs this to update their own name/avatar/contact info',
      },
      {
        key: 'team_members.manage_roles',
        label: 'Assign / Change Roles',
        description: 'Change which privilege role a user is assigned to',
      },
      {
        key: 'team_members.deactivate',
        label: 'Archive / Restore Members',
        description: 'Archiving and restoring are the same toggle — one key governs both',
      },
      { key: 'team_members.export', label: 'Export CSV' },
    ],
  },

  // TEAMS / ORG CHART — the standalone Teams section was deleted in the
  // People rebuild (v2.17); the `teams` / `team_assignments` tables are
  // gated by `team_members.*` on the server. The five `teams.*` keys were
  // dead toggles and were removed in the 2026-08-24 permissions cleanup.

  // ──────────────────────────────────────────────────────────────
  // CLIENTS / ORGANIZATIONS
  // ──────────────────────────────────────────────────────────────
  {
    id: 'organizations',
    label: 'Clients / Organizations',
    description: 'Manage client and organization records',
    permissions: [
      { key: 'organizations.view', label: 'View Clients' },
      { key: 'organizations.create', label: 'Create Clients' },
      // Contacts live inside the client record — they ride organizations.edit.
      { key: 'organizations.edit', label: 'Edit Clients' },
      {
        key: 'organizations.archive',
        label: 'Archive / Restore Clients',
        description: 'Soft-hide clients without deleting their data (and bring them back)',
      },
      { key: 'organizations.delete', label: 'Delete Clients' },
      { key: 'organizations.export', label: 'Export Client List' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // PROJECTS
  // ──────────────────────────────────────────────────────────────
  {
    id: 'projects',
    label: 'Projects',
    description: 'Project access and management',
    permissions: [
      {
        key: 'projects.view_own',
        label: 'View Assigned Projects',
        description: 'See only projects you are assigned to',
      },
      {
        key: 'projects.view_all',
        label: 'View All Projects',
        description: 'See every project in the firm',
      },
      {
        key: 'projects.view_financials',
        label: 'View Project Financials',
        description: 'See budget, hours burned, fees, and $ invoiced on projects they can view',
      },
      { key: 'projects.create', label: 'Create Projects' },
      // Field-level project gating (financials / schedule / scope / phases /
      // deliverables) was never built — every project field, phase row and
      // deliverable rides `projects.edit`. The five field keys were removed
      // in the 2026-08-24 cleanup rather than left as dead toggles.
      { key: 'projects.edit', label: 'Edit Projects' },
      {
        key: 'projects.archive',
        label: 'Archive / Restore Projects',
        description: 'Mark a project inactive without deleting its data (and bring it back)',
      },
      { key: 'projects.delete', label: 'Delete Projects' },
      {
        key: 'projects.assign_team',
        label: 'Assign Team to Projects',
        description: 'Add/remove people below you on the org chart',
      },
      {
        key: 'projects.assign_anyone',
        label: 'Assign Anyone to Projects',
        description: 'Add/remove any firm member to projects',
      },
      { key: 'projects.export', label: 'Export Project Data' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // SPECIFICATIONS (master specs)
  // ──────────────────────────────────────────────────────────────
  {
    id: 'specs',
    label: 'Master Specs',
    description: 'Firm-wide master specification library',
    permissions: [
      { key: 'specs.view', label: 'View Specs' },
      { key: 'specs.create', label: 'Create Specs' },
      // Variant work, block comments, submit-for-review and version history
      // all ride ordinary draft saves / spec view — their separate keys were
      // dead toggles and were removed in the 2026-08-24 cleanup.
      {
        key: 'specs.edit_own',
        label: 'Edit Own Drafts',
        description: 'Edit drafts you hold — not a draft someone else is editing',
      },
      {
        key: 'specs.edit_any',
        label: 'Edit Any Draft',
        description: 'Edit drafts held by anyone, including a draft someone else has open',
      },
      {
        key: 'specs.publish',
        label: 'Publish Specs',
        description: 'Move a spec into the published state (enforced at the API)',
      },
      {
        key: 'specs.approve_review',
        label: 'Approve / Reject Review Requests',
        description: 'Decide drafts that others submitted for review',
      },
      {
        // Phase 3 (docs/MASTERS_UNIFIED_PLAN.md): publishing an office master
        // raises an update for every client variant. Deciding those updates —
        // accept the new text or keep the client's deviation — is its own
        // responsibility, separate from authoring (specs.edit_*) and from
        // publishing (specs.publish). Server-enforced on both decide actions.
        key: 'specs.accept_updates',
        label: 'Accept / Reject Master Updates',
        description: "Decide updates raised on a client's version of a master spec",
      },
      {
        key: 'specs.archive',
        label: 'Archive / Restore Specs',
        description: 'Archiving and restoring are the same toggle — one key governs both',
      },
      { key: 'specs.delete', label: 'Delete Specs' },
      {
        key: 'specs.override_draft',
        label: "Override Others' Drafts",
        description: 'Discard or take over a draft another user has open',
      },
      {
        key: 'specs.import',
        label: 'Import from DOCX',
        description: 'Use the harvester to import specs from Word documents',
      },
      {
        key: 'specs.export',
        label: 'Export Specs',
        description: 'Render specs to Word — gates the server export endpoint',
      },
    ],
  },

  // LIBRARY MODULES — feature retired in v2.12.0. The transclusion model
  // ("edit one library module, all specs auto-update") was unused in
  // production and the editor's drag/coordinate model was fundamentally
  // broken because of it (expanded-vs-raw index mismatch). All references
  // were inlined by migration 019. Permission keys here are removed; any
  // role row still carrying them in its permissions[] array is silently
  // ignored.

  // SMART BLOCKS retired in v2.19 (docs/MASTERS_LADDER_PLAN.md §0 — the
  // feature is cut app-wide, so the privileges that gated it are gone too;
  // existing smart-token DATA still renders and still exports). Legacy
  // smart_blocks.* strings on stored roles are inert.

  // ──────────────────────────────────────────────────────────────
  // PROJECT SPECS (per-project spec forks)
  // ──────────────────────────────────────────────────────────────
  {
    id: 'project_specs',
    label: 'Project Specs',
    description: 'Per-project specification forks and issuances',
    permissions: [
      { key: 'project_specs.view', label: 'View Project Specs' },
      {
        key: 'project_specs.compile',
        label: 'Compile Masters into Projects',
        description: 'Fork master specs into a project',
      },
      { key: 'project_specs.edit_own', label: 'Edit Own Drafts' },
      { key: 'project_specs.edit_any', label: 'Edit Any Draft' },
      // Project specs have no review/publish lifecycle (no status column, no
      // review queue) — the submit/publish/approve/reject/override keys were
      // dead toggles and were removed in the 2026-08-24 cleanup. Re-add them
      // WITH server enforcement if the Spec Studio review flow ever lands.
      {
        key: 'project_specs.create_issuance',
        label: 'Create Issuances',
        description: 'Issue IFC / Addendum / ASI sets against a project',
      },
      {
        key: 'project_specs.archive',
        label: 'Archive / Restore Project Specs',
        description: 'Archiving and restoring are the same toggle — one key governs both',
      },
      { key: 'project_specs.delete', label: 'Delete Project Specs' },
      {
        key: 'project_specs.export',
        label: 'Export Project Specs',
        description: 'Render project specs to Word — gates the server export endpoint',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // MODULES (reusable block-groups)
  // ──────────────────────────────────────────────────────────────
  //
  // ⛔ A MODULE is a reusable block-group saved out of ONE spec and reused
  // in others — it is NOT a variant (a variant is a client version of a
  // master, gated by specs.*). Phase 4 of docs/MASTERS_UNIFIED_PLAN.md.
  //
  // Every key here is enforced server-side by the GENERIC table path:
  // `module_library` maps to this category in ec2-api/server.js TABLE_PERMS,
  // so list/query → modules.view, a new row → modules.create, an edit →
  // modules.edit, an archived_at-only flip → modules.archive, a row delete →
  // modules.delete. There are no bespoke module endpoints to forget about.
  //
  // Migration 056 added `module_categories` (the library's nested tree) and
  // `module_library.tags` — SAME category, no new keys: a category create
  // rides modules.create, rename/move/reorder and a module's category/tag
  // change ride modules.edit, a category delete (reparents, never deletes,
  // its own modules/children) rides modules.delete.
  {
    id: 'modules',
    label: 'Modules (Reusable Blocks)',
    description: 'Firm-wide library of reusable block-groups saved out of specs',
    permissions: [
      { key: 'modules.view', label: 'View the Module Library' },
      {
        key: 'modules.create',
        label: 'Save Modules',
        description:
          'Capture selected blocks out of a spec as a named module, and create categories',
      },
      {
        key: 'modules.edit',
        label: 'Rename / Edit Modules',
        description: 'Edit modules, their category and tags; rename and move categories',
      },
      {
        // SYNC (docs/SYNC_PLAN.md D14): a module edits in a shared draft;
        // Publish moves the draft into the library and offers the change to
        // every copy. Server-enforced on `module_publish`.
        key: 'modules.publish',
        label: 'Publish Modules',
        description: 'Publish a module draft to the library and offer the update to every copy',
      },
      {
        key: 'modules.archive',
        label: 'Archive / Restore Modules',
        description: 'Archiving and restoring are the same toggle — one key governs both',
      },
      {
        key: 'modules.delete',
        label: 'Delete Modules',
        description:
          'Delete modules, and delete categories (their modules and any child categories move up a level)',
      },
      { key: 'modules.export', label: 'Export CSV' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // FORMAS (formatting templates)
  // ──────────────────────────────────────────────────────────────
  {
    id: 'formas',
    label: 'Master Formas',
    description: 'Word formatting templates and token configurations',
    permissions: [
      { key: 'formas.view', label: 'View Formas' },
      { key: 'formas.create', label: 'Create Formas' },
      { key: 'formas.edit', label: 'Edit Formas' },
      {
        // SYNC (docs/SYNC_PLAN.md): Publish is the only road out of a forma
        // draft; also decides Review items raised on project formas.
        key: 'formas.publish',
        label: 'Publish Formas',
        description: 'Publish forma drafts and decide updates raised on project formas',
      },
      // Duplicating a Forma IS a create — `formas.duplicate` was a dead
      // toggle and was removed in the 2026-08-24 cleanup.
      {
        key: 'formas.set_default',
        label: 'Set Default Forma',
        description: 'Choose the firm-wide default formatting template',
      },
      { key: 'formas.archive', label: 'Archive / Restore Formas' },
      { key: 'formas.delete', label: 'Delete Formas' },
      { key: 'formas.import_template', label: 'Import .dotx Templates' },
      { key: 'formas.export_template', label: 'Export .dotx Templates' },
      {
        key: 'formas.apply_to_spec',
        label: 'Apply Forma to Spec',
        description: 'Choose which Forma a spec exports with',
      },
      { key: 'formas.export', label: 'Export CSV' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // COMPILE (bulk spec compilation)
  // ──────────────────────────────────────────────────────────────
  {
    id: 'compile',
    label: 'Compile Specs',
    description: 'Bulk spec compilation and project generation',
    permissions: [
      { key: 'compile.view', label: 'View Compile Page' },
      {
        key: 'compile.run',
        label: 'Run Compile',
        description: 'Compile selected specs into a project — one spec or many',
      },
      // compile.bulk (same run path) and compile.export (that's the
      // project-spec export) were dead toggles — removed 2026-08-24.
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // FORMATTER (document formatter)
  // ──────────────────────────────────────────────────────────────
  {
    id: 'formatter',
    label: 'Document Formatter',
    description: 'Standalone Word document reformatting',
    permissions: [
      { key: 'formatter.view', label: 'View Formatter' },
      {
        key: 'formatter.run',
        label: 'Format Documents',
        description: 'Reformat Word documents with a Forma — one document or a batch',
      },
      // formatter.batch was the same run path — removed 2026-08-24.
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // TIMESHEETS
  // ──────────────────────────────────────────────────────────────
  {
    id: 'timesheets',
    label: 'Timesheets',
    description: 'Time entry, submission, and approval',
    permissions: [
      { key: 'timesheets.view_own', label: 'View Own Timesheets' },
      {
        key: 'timesheets.view_team',
        label: 'View Team Timesheets',
        description: 'See timesheets for people below you on the org chart',
      },
      { key: 'timesheets.view_all', label: 'View All Timesheets' },
      { key: 'timesheets.create', label: 'Enter Time' },
      {
        key: 'timesheets.edit_own',
        label: 'Edit Own Entries',
        description: 'Edit your own time entries before submitting',
      },
      {
        key: 'timesheets.edit_any',
        label: "Edit Others' Entries",
        description: 'Edit time entries that belong to someone else',
      },
      // Submitting is a save of your OWN sheet (rides edit_own); rejecting is
      // the other half of approving — both keys removed 2026-08-24.
      {
        key: 'timesheets.approve',
        label: 'Approve / Reject Timesheets',
        description: 'Decide submitted timesheets — also gates Lock',
      },
      {
        key: 'timesheets.unlock',
        label: 'Unlock Submitted / Approved Timesheets',
        description: 'Re-open a locked timesheet so it can be edited',
      },
      { key: 'timesheets.export', label: 'Export Timesheets' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // VACATION / TIME OFF
  // ──────────────────────────────────────────────────────────────
  {
    id: 'vacation',
    label: 'Vacation / Time Off',
    description: 'Vacation requests and approval',
    permissions: [
      { key: 'vacation.view_own', label: 'View Own Requests' },
      { key: 'vacation.view_team', label: 'View Team Requests' },
      { key: 'vacation.view_all', label: 'View All Requests' },
      { key: 'vacation.create', label: 'Submit Requests' },
      {
        key: 'vacation.edit',
        label: 'Edit Requests',
        description:
          'Modify a pending request before it is decided (your own unless you also hold Approve)',
      },
      { key: 'vacation.approve', label: 'Approve / Deny Requests' },
      // Cancelling a pending request is an edit of your own row — the
      // separate `vacation.cancel` toggle was removed 2026-08-24.
      {
        key: 'vacation.delete',
        label: 'Delete Requests',
        description:
          'Permanently delete a decided request — anyone may delete their OWN request while it is still pending',
      },
      { key: 'vacation.export', label: 'Export CSV' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // INVOICES
  // ──────────────────────────────────────────────────────────────
  {
    id: 'invoices',
    label: 'Invoices',
    description: 'Invoice creation, sending, and AR tracking (no payment processing)',
    permissions: [
      {
        key: 'invoices.view_own',
        label: 'View Project Invoices',
        description: 'Invoices for projects you are on',
      },
      {
        key: 'invoices.view_all',
        label: 'View All Invoices',
        description: 'Firm-wide invoices, including projects you are not on',
      },
      {
        key: 'invoices.view_financials',
        label: 'View Invoice Amounts',
        description: 'See $ totals — some roles see invoice existence but not amounts',
      },
      {
        key: 'invoices.create',
        label: 'Create Invoices',
        description: 'Including generating a draft invoice from a project',
      },
      { key: 'invoices.edit', label: 'Edit Invoices' },
      { key: 'invoices.send', label: 'Send Invoices', description: 'Mark sent / generate PDF' },
      {
        key: 'invoices.mark_paid',
        label: 'Record Payments',
        description: 'Mark partial or full payment received',
      },
      { key: 'invoices.void', label: 'Void Invoices' },
      {
        key: 'invoices.archive',
        label: 'Archive Invoices',
        description: 'Hide paid or cancelled invoices from active lists',
      },
      { key: 'invoices.delete', label: 'Delete Invoices' },
      { key: 'invoices.export_pdf', label: 'Export PDF' },
      { key: 'invoices.export_csv', label: 'Export CSV' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // REPORTS
  // ──────────────────────────────────────────────────────────────
  {
    id: 'reports',
    label: 'Reports & Analytics',
    description: 'Run, view, and export firm reports',
    permissions: [
      { key: 'reports.view', label: 'Open Reports Page' },
      {
        key: 'reports.view_financials',
        label: 'View Financial Figures',
        description: 'Revenue, margin, burn, AR, and any $ values inside reports',
      },
      { key: 'reports.run_utilization', label: 'Run Utilization Report' },
      {
        key: 'reports.run_wip',
        label: 'Run WIP Report',
        description: 'Work-in-progress: billable hours not yet invoiced',
      },
      {
        key: 'reports.run_profitability',
        label: 'Run Project Profitability Report',
        description: 'Actual cost vs budget per project',
      },
      { key: 'reports.run_ar_aging', label: 'Run AR Aging Report' },
      { key: 'reports.run_time_by_project', label: 'Run Time By Project Report' },
      { key: 'reports.run_time_by_person', label: 'Run Time By Person Report' },
      // reports.run_forecast (no such report) and reports.schedule (no
      // scheduling feature) were removed 2026-08-24 — re-add when they ship.
      { key: 'reports.export_csv', label: 'Export Report As CSV' },
      { key: 'reports.export_pdf', label: 'Export Report As PDF' },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // DASHBOARDS — saved views and per-user dashboard customization
  // ──────────────────────────────────────────────────────────────
  {
    id: 'dashboards',
    label: 'Dashboards & Saved Views',
    description: 'Personal dashboards, saved filter/grouping arrangements per user',
    permissions: [
      // The view_own/create/edit_own/delete keys exist so the standard backend
      // table-permission check can treat dashboard_views like any other
      // self-scoped table. Every regular user gets these via BASE_PERMISSIONS.
      { key: 'dashboards.view_own', label: 'View Own Saved Views' },
      {
        key: 'dashboards.view_all',
        label: 'View All Saved Views',
        description: 'Includes others private personal views — admin tier',
      },
      { key: 'dashboards.create', label: 'Create Saved Views' },
      { key: 'dashboards.edit_own', label: 'Edit Own Saved Views' },
      { key: 'dashboards.edit_any', label: "Edit Any User's Saved View" },
      {
        key: 'dashboards.delete',
        label: 'Delete Saved Views',
        description:
          "Delete your own saved views. Deleting someone ELSE's view needs Edit Any User's Saved View",
      },
      // dashboards.customize (widget arrangement never checked it) and
      // dashboards.manage_firm_default (firm defaults live in firm_prefs,
      // gated by admin_center.manage_firm_settings) removed 2026-08-24.
      {
        key: 'dashboards.share_views',
        label: 'Publish Shared Views',
        description: 'Make a saved view visible to other users in the firm',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // FORECASTING
  // ──────────────────────────────────────────────────────────────
  {
    id: 'forecasting',
    label: 'Forecasting',
    description: 'Hour forecasting and resource planning grid',
    permissions: [
      { key: 'forecasting.view_own', label: 'View Own Forecasts' },
      {
        key: 'forecasting.view_team',
        label: 'View Team Forecasts',
        description: 'Team leads + PMs: see forecasts for people on the projects/teams they lead',
      },
      {
        key: 'forecasting.view_all',
        label: 'View All Forecasts',
        description: 'Firm-wide grid (admins, principals, billing roles)',
      },
      { key: 'forecasting.edit_own', label: 'Edit Own Forecasts' },
      {
        key: 'forecasting.edit_team',
        label: 'Edit Team Forecasts',
        description:
          'Team leads + PMs: assign hours to people on their projects. Server enforces project-membership.',
      },
      { key: 'forecasting.edit_any', label: 'Edit Any Forecast' },
      {
        key: 'forecasting.delete_team',
        label: 'Delete Team Forecasts',
        description: 'Delete forecast rows for people on your projects',
      },
      { key: 'forecasting.delete_any', label: 'Delete Any Forecast' },
      {
        key: 'forecasting.bulk_assign',
        label: 'Bulk-Assign Forecasts',
        description: 'Drag-paint cell ranges, copy-week, paste-week affordances',
      },
      // Forecast view sharing runs through dashboards.share_views (saved
      // views are one mechanism) — forecasting.share_views removed 2026-08-24.
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // TASKS
  // ──────────────────────────────────────────────────────────────
  {
    id: 'tasks',
    label: 'Tasks',
    description: 'Task management and assignment',
    permissions: [
      {
        key: 'tasks.view_own',
        label: 'View Own Tasks',
        description: 'Tasks you created or are assigned to (and their subtasks)',
      },
      {
        key: 'tasks.view_team',
        label: 'View Team Tasks',
        description:
          'Tasks created by or assigned to people below you on the org chart, plus your own',
      },
      { key: 'tasks.view_all', label: 'View All Tasks', description: 'See every task in the firm' },
      {
        key: 'tasks.create',
        label: 'Create Tasks',
        description: 'Create tasks, and comment on tasks you can see',
      },
      {
        key: 'tasks.edit_own',
        label: 'Edit Own Tasks',
        description:
          'Edit tasks you created or are assigned to, including marking them done, and their links, files and subtasks',
      },
      {
        key: 'tasks.edit_any',
        label: 'Edit Any Task',
        description: 'Edit any task you can see, with its links, files and subtasks',
      },
      {
        key: 'tasks.archive',
        label: 'Archive / Restore Tasks',
        description: 'Archive tasks you can see without deleting them (and bring them back)',
      },
      {
        key: 'tasks.delete',
        label: 'Delete Tasks',
        description: 'Delete tasks you can see, with their subtasks, comments, links and files',
      },
      {
        key: 'tasks.assign_team',
        label: 'Assign Team Tasks',
        description: 'Assign tasks to people below you on the org chart',
      },
      {
        key: 'tasks.assign_anyone',
        label: 'Assign Anyone Tasks',
        description: 'Assign tasks to any firm member',
      },
      { key: 'tasks.export', label: 'Export CSV' },
      // Completing a task is an edit; commenting rides tasks.create — both
      // toggles were dead and were removed 2026-08-24.
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // EXPENSES
  // ──────────────────────────────────────────────────────────────
  {
    id: 'expenses',
    label: 'Expenses',
    description: 'Expense tracking and approval',
    permissions: [
      { key: 'expenses.view_own', label: 'View Own Expenses' },
      { key: 'expenses.view_team', label: 'View Team Expenses' },
      { key: 'expenses.view_all', label: 'View All Expenses' },
      { key: 'expenses.create', label: 'Submit Expenses' },
      { key: 'expenses.edit_own', label: 'Edit Own Expenses' },
      { key: 'expenses.edit_any', label: "Edit Others' Expenses" },
      { key: 'expenses.approve', label: 'Approve / Reject Expenses' },
      {
        key: 'expenses.delete',
        label: 'Delete Expenses',
        description:
          'Permanently delete a submitted or approved expense — anyone may delete their OWN draft',
      },
      { key: 'expenses.export', label: 'Export Expenses' },
    ],
  },

  // FILES / ATTACHMENTS — there is no files feature; attachments ride the
  // tasks / expenses gates. The five `files.*` keys were dead toggles and
  // were removed in the 2026-08-24 cleanup.

  // ──────────────────────────────────────────────────────────────
  // AUDIT LOG
  // ──────────────────────────────────────────────────────────────
  {
    id: 'audit_log',
    label: 'Audit Log',
    description: 'Activity history and change logs',
    permissions: [
      { key: 'audit_log.view', label: 'View Audit Log' },
      {
        key: 'audit_log.view_all',
        label: "View All Users' Activity",
        description: 'See audit entries for every user, not just your own',
      },
      // No audit-log export exists — key removed 2026-08-24.
    ],
  },

  // ──────────────────────────────────────────────────────────────
  // SYSTEM — virtual permissions exposing backend-implicit behavior
  // ──────────────────────────────────────────────────────────────
  {
    id: 'system',
    label: 'System',
    description:
      'Platform-level and implicit permissions. Normally granted to every role — toggle off only for extremely restricted accounts.',
    permissions: [
      {
        key: 'system.self_edit',
        label: 'Edit Own Record',
        description:
          'Everyone needs this to update their own profile fields, notification prefs, etc. Turning this off creates a view-only account.',
      },
      {
        key: 'system.lookup_read',
        label: 'Read Lookup Tables',
        description:
          'Access read-only lookup data the app needs to render basic UI (project types, roles, categories). Turning this off breaks most views.',
      },
      // system.notifications had no enforcement point anywhere — removed
      // 2026-08-24. Re-add it WITH a check when notifications ship.
    ],
  },
];

// ─── Flat list of all permission keys ───

export const ALL_PERMISSIONS: string[] = PERMISSION_CATEGORIES.flatMap((c) =>
  c.permissions.map((p) => p.key)
);

// ─── Permission check helper ───

/**
 * Check whether a permission array grants a specific key.
 *
 * The reserved key `'*'` in a permissions array is a super-admin wildcard —
 * it grants every permission in the system, present and future. Used by the
 * site_admin preset so we never have to chase newly-added permissions.
 */
export function hasPermission(permissions: string[] | undefined, key: string): boolean {
  if (!permissions) return false;
  if (permissions.includes('*')) return true;
  return permissions.includes(key);
}

/**
 * Check whether ANY of several permission keys is granted.
 */
export function hasAnyPermission(permissions: string[] | undefined, keys: string[]): boolean {
  if (!permissions) return false;
  if (permissions.includes('*')) return true;
  return keys.some((k) => permissions.includes(k));
}

// ─── Retired keys → their successors ───
//
// Stored `roles.permissions` rows are NEVER rewritten by a catalog change
// (prod data is stripped only by an explicitly approved migration), so a
// role row can carry a key that no longer exists. Rather than silently
// dropping the capability, both the server (loadPerms/buildAuthUser) and
// the client expand these on load: the old key still means what it meant.
// Keys with no successor (files.*, teams.*, audit_log.export,
// system.notifications, …) expand to nothing — they gated nothing.
export const LEGACY_KEY_EXPANSIONS: Record<string, string[]> = {
  // Pre-split edit keys.
  'specs.edit': ['specs.edit_own', 'specs.edit_any'],
  'tasks.edit': ['tasks.edit_own', 'tasks.edit_any'],
  'timesheets.edit': ['timesheets.edit_own', 'timesheets.edit_any'],
  'expenses.edit': ['expenses.edit_own', 'expenses.edit_any'],
  'forecasting.create': ['forecasting.edit_own', 'forecasting.edit_team'],
  'forecasting.edit': ['forecasting.edit_own', 'forecasting.edit_team', 'forecasting.edit_any'],
  'forecasting.delete': ['forecasting.delete_team', 'forecasting.delete_any'],
  'invoices.view': ['invoices.view_own'],
  'invoices.export': ['invoices.export_csv', 'invoices.export_pdf'],
  'reports.view_utilization': ['reports.run_utilization'],
  'reports.view_wip': ['reports.run_wip'],
  'reports.export': ['reports.export_csv', 'reports.export_pdf'],
  // Retired 2026-08-24 (permissions cleanup) — folded into the key that
  // actually governs the action.
  'team_members.invite': ['team_members.create'],
  'team_members.reactivate': ['team_members.deactivate'],
  'organizations.edit_contacts': ['organizations.edit'],
  'organizations.restore': ['organizations.archive'],
  'projects.restore': ['projects.archive'],
  'projects.edit_financials': ['projects.edit'],
  'projects.edit_schedule': ['projects.edit'],
  'projects.edit_scope': ['projects.edit'],
  'projects.manage_phases': ['projects.edit'],
  'projects.manage_deliverables': ['projects.edit'],
  'projects.view_all_financials': ['projects.view_financials'],
  'specs.view_history': ['specs.view'],
  'specs.restore': ['specs.archive'],
  'specs.reject_review': ['specs.approve_review'],
  'formas.duplicate': ['formas.create'],
  'compile.bulk': ['compile.run'],
  'compile.export': ['compile.run'],
  'formatter.batch': ['formatter.run'],
  'timesheets.reject': ['timesheets.approve'],
  'expenses.reject': ['expenses.approve'],
  'vacation.cancel': ['vacation.edit'],
  'invoices.generate_from_project': ['invoices.create'],
  'forecasting.share_views': ['dashboards.share_views'],
  'tasks.complete': ['tasks.edit_own'],
};

/** Expand any retired keys in a stored permission array into their current
 *  successors. Idempotent; never removes anything. */
export function expandLegacyPermissions(permissions: string[] | undefined): string[] {
  if (!Array.isArray(permissions)) return [];
  const out = new Set(permissions);
  for (const p of permissions) {
    const expansion = LEGACY_KEY_EXPANSIONS[p];
    if (expansion) expansion.forEach((k) => out.add(k));
  }
  return [...out];
}

// ─── Tier gating: permission category → licensed module ───
//
// A firm only holds the modules it bought (`firm_modules`). Keys whose
// module the firm lacks are not grantable in the role builder and evaluate
// false at check-time. `core` is always enabled.
export const PERMISSION_CATEGORY_MODULES: Record<string, AppModule> = {
  admin_center: 'core',
  team_members: 'core',
  dashboards: 'core',
  audit_log: 'core',
  system: 'core',
  formas: 'forma',
  formatter: 'forma',
  specs: 'spec_manager',
  project_specs: 'spec_manager',
  // The module library rides the same tier as the specs it is cut from.
  modules: 'spec_manager',
  compile: 'spec_manager',
  projects: 'project_hub',
  organizations: 'project_hub',
  tasks: 'project_hub',
  timesheets: 'time_billing',
  vacation: 'time_billing',
  expenses: 'time_billing',
  forecasting: 'time_billing',
  invoices: 'time_billing',
  reports: 'time_billing',
};

/** The module a permission key belongs to. Unknown categories are treated
 *  as `core` (never hide a key we forgot to map). */
export function permissionModule(key: string): AppModule {
  const cat = key.split('.')[0];
  return PERMISSION_CATEGORY_MODULES[cat] || 'core';
}

/** Is this key inside the firm's licensed modules? `core` always is.
 *  An empty/unknown module list is treated as "everything enabled" so a
 *  firm whose modules were never seeded is never locked out. */
export function isPermissionInModules(
  key: string,
  enabledModules: AppModule[] | undefined
): boolean {
  const mod = permissionModule(key);
  if (mod === 'core') return true;
  if (!enabledModules || enabledModules.length === 0) return true;
  return enabledModules.includes(mod);
}

// ─── Base permissions every user needs to log in and operate ───
//
// Editing your own profile, reading lookup tables, receiving notifications —
// without these the app is unusable. Every preset inherits them. Admins can
// strip them from custom roles only if they explicitly want an audit-only
// or read-only-kiosk account.

export const BASE_PERMISSIONS: string[] = [
  'system.self_edit',
  'system.lookup_read',
  'team_members.edit_own',
  'organizations.view',
  'timesheets.view_own',
  'timesheets.create',
  'timesheets.edit_own',
  'tasks.view_own',
  'tasks.edit_own',
  'expenses.view_own',
  'expenses.create',
  'expenses.edit_own',
  'vacation.view_own',
  'vacation.create',
  // T11 Part 2: `projects.view_own`, `vacation.edit`, `tasks.create` dropped
  // from BASE (true floor — a read-only-kiosk custom role shouldn't get
  // "submit time off" or "create a task" for free). Every preset that
  // relied on them now carries them explicitly (see each preset below).
  // Forecasting + dashboards: every user can view their own forecast and
  // save personal views — those are read-only-of-self capabilities.
  'forecasting.view_own',
  'forecasting.edit_own',
  'dashboards.view_own',
  'dashboards.create',
  'dashboards.edit_own',
  // Own-view delete rides the personal-artifact half of the lifecycle
  // deletion law (server: owner may hard-delete an UNSHARED own view);
  // deleting somebody else's view needs dashboards.edit_any.
  'dashboards.delete',
];

// ─── Role presets (templates for creating new roles) ───
//
// These are STARTING POINTS. Custom roles are the product direction; admins
// can clone any of these, rename, and trim the permission set for their firm's
// policies. The admin UI surfaces these as a "clone from preset" dropdown
// in the role editor.
//
// site_admin uses the '*' wildcard so future-added permissions automatically
// apply. Everyone else has their explicit set — deliberate choice so admins
// can see exactly what they're granting.

export const ROLE_PRESETS: Record<string, { label: string; permissions: string[] }> = {
  site_admin: {
    label: 'Site Admin (Full Access)',
    permissions: ['*'],
  },

  admin: {
    label: 'Administrator',
    // Everything except hard-delete, rollback, and override actions — soft
    // delete (archive/restore) is fine for admins. Admins can approve
    // reviews, manage team, manage billing, but not force-unlock another
    // user's draft or rollback a published version.
    permissions: ALL_PERMISSIONS.filter(
      (p) =>
        !p.endsWith('.delete') &&
        !p.endsWith('.force_unlock') &&
        !p.endsWith('.version_rollback') &&
        !p.endsWith('.override_draft')
    ),
  },

  publisher: {
    label: 'Publisher',
    // Senior spec/project oversight. Can review, publish, approve, manage
    // the library, but not administer the firm.
    permissions: [
      ...BASE_PERMISSIONS,
      'team_members.view',
      'organizations.view',
      'organizations.create',
      'organizations.edit',
      'organizations.archive',
      'projects.view_all',
      'projects.view_financials',
      'projects.create',
      'projects.edit',
      'projects.archive',
      'projects.assign_team',
      'projects.assign_anyone',
      'projects.export',
      'specs.view',
      'specs.create',
      'specs.edit_own',
      'specs.edit_any',
      'specs.publish',
      'specs.approve_review',
      'specs.accept_updates',
      'specs.archive',
      'specs.override_draft',
      'specs.import',
      'specs.export',
      // library_modules.* removed in v2.12.0 — feature retired.
      'project_specs.view',
      'project_specs.compile',
      'project_specs.edit_own',
      'project_specs.edit_any',
      'project_specs.create_issuance',
      'project_specs.archive',
      'project_specs.export',
      // Modules (reusable block-groups) mirror the project_specs ladder:
      // everyone who reads specs reads the library, hands-on tiers save and
      // rename, senior oversight archives. `.delete` is deliberately absent
      // — like every other `.delete`, it stays with site admin.
      'modules.view',
      'modules.create',
      'modules.edit',
      'modules.publish',
      'modules.archive',
      'formas.view',
      'formas.publish',
      'formas.apply_to_spec',
      'compile.view',
      'compile.run',
      'formatter.view',
      'formatter.run',
      'timesheets.view_team',
      'timesheets.approve',
      'vacation.view_team',
      'vacation.approve',
      // BASE backfill (T11 Part 2): BASE no longer grants these; keep them
      // explicit so dropping BASE doesn't silently take away "create a
      // task" / "submit time off".
      'tasks.create',
      'vacation.edit',
      'reports.view',
      // Bug 8 (stale preset keys): reports.view_utilization / view_wip /
      // export were legacy strings the Roles UI could not display and the
      // client never expanded — replaced with the current keys.
      'reports.run_utilization',
      'reports.run_wip',
      'reports.export_csv',
      'reports.export_pdf',
      'forecasting.view_own',
      'forecasting.view_team',
      'forecasting.edit_own',
      'forecasting.edit_team',
      'forecasting.bulk_assign',
      'dashboards.share_views',
      'tasks.view_team',
      'tasks.view_all',
      'tasks.edit_any',
      'tasks.archive',
      'tasks.assign_team',
      'tasks.assign_anyone',
      'expenses.view_team',
      'expenses.approve',
      'audit_log.view',
    ],
  },

  project_manager: {
    label: 'Project Manager',
    permissions: [
      ...BASE_PERMISSIONS,
      'team_members.view',
      'organizations.view',
      'organizations.create',
      'organizations.edit',
      'organizations.archive',
      'projects.view_all',
      'projects.view_financials',
      'projects.create',
      'projects.edit',
      'projects.archive',
      'projects.assign_team',
      'projects.assign_anyone',
      'projects.export',
      'specs.view',
      'specs.create',
      'specs.edit_own',
      'specs.archive',
      'specs.export',
      // library_modules.* removed in v2.12.0 — feature retired.
      'project_specs.view',
      'project_specs.compile',
      'project_specs.edit_own',
      'project_specs.archive',
      'project_specs.export',
      'modules.view',
      'modules.create',
      'modules.edit',
      'modules.archive',
      'formas.view',
      'formas.apply_to_spec',
      'compile.view',
      'compile.run',
      'formatter.view',
      'formatter.run',
      'timesheets.view_team',
      'timesheets.approve',
      'vacation.view_team',
      'vacation.approve',
      // BASE backfill (T11 Part 2): see Publisher comment above.
      'tasks.create',
      'vacation.edit',
      // Bug 8 (stale preset keys): invoices.view / reports.export were
      // legacy strings — replaced with the current keys.
      'invoices.view_own',
      'reports.view',
      'reports.export_csv',
      'reports.export_pdf',
      'forecasting.view_own',
      'forecasting.view_team',
      'forecasting.edit_own',
      'forecasting.edit_team',
      'forecasting.bulk_assign',
      'dashboards.share_views',
      'tasks.view_team',
      'tasks.view_all',
      'tasks.edit_any',
      'tasks.archive',
      'tasks.assign_team',
      'tasks.assign_anyone',
      'expenses.view_team',
      'expenses.approve',
    ],
  },

  editor: {
    label: 'Editor',
    // Hands-on contributor. Edits everything, submits for review, but doesn't
    // publish or approve.
    permissions: [
      ...BASE_PERMISSIONS,
      'team_members.view',
      'organizations.view',
      'projects.view_own',
      'specs.view',
      'specs.create',
      'specs.edit_own',
      'specs.export',
      // library_modules.* removed in v2.12.0 — feature retired.
      'project_specs.view',
      'project_specs.edit_own',
      'project_specs.export',
      'modules.view',
      'modules.create',
      'modules.edit',
      'formas.view',
      'formas.apply_to_spec',
      'compile.view',
      'formatter.view',
      'formatter.run',
      'tasks.edit_own',
      // BASE backfill (T11 Part 2): see Publisher comment above.
      'tasks.create',
      'vacation.edit',
    ],
  },

  staff: {
    label: 'Staff Member',
    permissions: [
      ...BASE_PERMISSIONS,
      'team_members.view',
      'specs.view',
      'specs.edit_own',
      'specs.export',
      'project_specs.view',
      'project_specs.export',
      'modules.view',
      'formas.view',
      'tasks.edit_own',
      // BASE backfill (T11 Part 2): see Publisher comment above. Staff also
      // needs `projects.view_own` explicit — unlike Publisher/PM it has no
      // `projects.view_all` to fall back on.
      'tasks.create',
      'vacation.edit',
      'projects.view_own',
      // `specs.create` removed (T11 Part 2 resolution 3): Staff can edit
      // their own drafts but not originate a new spec.
    ],
  },

  viewer: {
    label: 'View Only',
    permissions: [
      'system.self_edit',
      'system.lookup_read',
      'team_members.view',
      'team_members.edit_own',
      'organizations.view',
      'projects.view_own',
      'specs.view',
      'project_specs.view',
      'modules.view',
      'formas.view',
      'timesheets.view_own',
      'tasks.view_own',
      'expenses.view_own',
      'vacation.view_own',
    ],
  },
};

// ─── Legacy boolean → permissions mapping (for data migration) ───
//
// Kept so we can one-shot migrate any leftover role rows in the DB that were
// seeded with the old boolean shape. Not used for live permission checks —
// frontend always goes through hasPermission() against the array.

export const LEGACY_TO_PERMISSIONS: Record<string, string[]> = {
  canAccessAdmin: [
    'admin_center.view',
    'admin_center.manage_roles',
    'admin_center.manage_billing',
    'admin_center.manage_subscription',
    'audit_log.view',
    'timesheets.view_all',
    'timesheets.edit_any',
    'timesheets.approve',
    'vacation.view_all',
    'vacation.approve',
    'expenses.view_all',
    'expenses.edit_any',
    'expenses.approve',
  ],
  canAccessTeam: [
    'team_members.view',
    'team_members.create',
    'team_members.edit',
    'team_members.deactivate',
  ],
  canAccessInvoices: [
    'invoices.view_own',
    'invoices.view_all',
    'invoices.create',
    'invoices.edit',
    'invoices.send',
    'invoices.delete',
    'invoices.export_csv',
    'invoices.export_pdf',
  ],
  canAccessAllProjects: [
    'projects.view_all',
    'tasks.view_team',
    'tasks.view_all',
    'tasks.edit_any',
    'tasks.assign_team',
    'tasks.assign_anyone',
    'forecasting.view_own',
    'forecasting.view_all',
    'forecasting.edit_own',
    'forecasting.edit_any',
  ],
  canAccessBilling: ['reports.view', 'reports.export_csv', 'reports.export_pdf'],
  canManageSpecs: [
    'specs.view',
    'specs.create',
    'specs.edit_own',
    'specs.edit_any',
    'specs.delete',
    'specs.publish',
    'specs.archive',
    'specs.export',
    // SYNC: whoever may publish specs may publish modules and formas.
    'modules.publish',
    'formas.publish',
  ],
  canEditProjects: [
    'projects.create',
    'projects.edit',
    'projects.assign_team',
    'projects.assign_anyone',
    'organizations.create',
    'organizations.edit',
  ],
  canDeleteProjects: ['projects.delete'],
  canDeleteClients: ['organizations.delete'],
};

// ─── Derive legacy booleans from permissions (for backward compat) ───
//
// @deprecated — any code still reading these should migrate to direct
// hasPermission() calls against specific keys. Kept only until every
// caller is converted. See the permissions audit for the migration plan.

export interface LegacyPermFlags {
  canAccessAdmin: boolean;
  canAccessTeam: boolean;
  canAccessInvoices: boolean;
  canAccessAllProjects: boolean;
  canAccessBilling: boolean;
  canManageSpecs: boolean;
  canEditProjects: boolean;
  canDeleteProjects: boolean;
  canDeleteClients: boolean;
}

export function permissionsToLegacy(perms: string[]): LegacyPermFlags {
  const has = (k: string) => hasPermission(perms, k);
  return {
    canAccessAdmin:
      has('admin_center.view') ||
      has('admin_center.manage_roles') ||
      has('admin_center.manage_billing') ||
      has('admin_center.manage_subscription'),
    canAccessTeam: has('team_members.view'),
    // `invoices.view` is a legacy key (expanded to invoices.view_own on
    // load) — accept either so old role rows keep working.
    canAccessInvoices: has('invoices.view') || has('invoices.view_own') || has('invoices.view_all'),
    canAccessAllProjects: has('projects.view_all'),
    canAccessBilling: has('reports.view'),
    canManageSpecs: has('specs.view'),
    canEditProjects: has('projects.edit'),
    canDeleteProjects: has('projects.delete'),
    canDeleteClients: has('organizations.delete'),
  };
}

/** One-shot migration helper: convert old `canXxx: true` role rows into a
 *  permissions array. Only used by data-fix scripts. */
export function legacyToPermissions(flags: Record<string, boolean>): string[] {
  const perms = new Set<string>(BASE_PERMISSIONS);
  for (const [flag, keys] of Object.entries(LEGACY_TO_PERMISSIONS)) {
    if (flags[flag]) {
      keys.forEach((k) => perms.add(k));
    }
  }
  return [...perms].sort();
}
